<template>
  <v-app>

    <!-- ================= SEARCH PANEL ================= -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Search Form
              </v-expansion-panel-title>

              <v-expansion-panel-text>
                <v-form @submit.prevent="submit">
                  <v-row class="px-2 pt-4 pb-0">
                    <v-col md="2" cols="12">
                      <v-date-input
                        label="From Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        v-model="from_date.value.value"
                        :error-messages="from_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col md="2" cols="12">
                      <v-date-input
                        label="To Date"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        prepend-icon=""
                        v-model="to_date.value.value"
                        :error-messages="to_date.errorMessage.value"
                        clearable
                      />
                    </v-col>

                    <v-col md="2" cols="12">
                      <v-btn type="submit" color="primary" class="mt-1">
                        Search
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- ================= TABLE PANEL ================= -->
    <v-container fluid style="margin-top:-20px">
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title
                color="blue-grey-lighten-4"
                class="panel-header"
              >
                Textile Order Records
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <!-- Top Actions -->
                <v-row class="py-2">
                  <v-col md="4">
                    <v-text-field
                      v-model="search"
                      label="Search"
                      append-inner-icon="mdi-magnify"
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      clearable
                    />
                  </v-col>

                  <v-col md="5" class="text-center">
                    <v-btn-group density="compact" divided>
                      <v-btn color="success" @click="exportToCSV">CSV</v-btn>
                      <v-btn color="primary" @click="exportToExcel">EXCEL</v-btn>
                      <v-btn color="red" @click="exportToPDF">PDF</v-btn>
                    </v-btn-group>
                  </v-col>

                  <v-col md="3" class="text-right">
                    <v-btn
                      color="primary"
                      prepend-icon="mdi-plus"
                      rounded="xl"
                      @click="goToCreate"
                    >
                      Add Textile Order
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- ================= DATA TABLE ================= -->
                <v-data-table
                  :headers="headers"
                  :items="dyeingOrderList"
                  :loading="loading"
                  :search="search"
                  v-model:items-per-page="itemsPerPage"
                  density="compact"
                >
                  <!-- Index -->
                  <template #item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <!-- Style -->
                  <template #item.buyer_name="{ item }">
                    {{ (item.buyer_order?.buyer?.name)+' - '+(item.buyer_order?.buyer?.code) }}
                  </template>

                  <!-- Color -->
                  <template #item.color="{ item }">
                    {{ item.color?.name || '-' }}
                  </template>

                  <!-- Dyeing House -->
                  <template #item.dyeing_house="{ item }">
                    {{ item.dyeing_house?.name || '-' }}
                  </template>

                  <!-- Order Qty -->
                  <template #item.order_qty_kg="{ item }">
                    {{ Number(item.order_qty_kg).toFixed(2) }}
                  </template>

                  <!-- Dyeing Status -->
                  <template #item.dyeing_status="{ item }">
                    <v-chip
                      size="small"
                      :color="item.dyeing_status === 'running' ? 'blue' : 'green'"
                    >
                      {{ item.dyeing_status }}
                    </v-chip>
                  </template>

                  <!-- Status -->
                  <template #item.status="{ item }">
                    <v-chip
                      size="small"
                      :color="item.status === 1 ? 'success' : 'red'"
                    >
                      {{ item.status === 1 ? 'Active' : 'Inactive' }}
                    </v-chip>
                  </template>

                  <!-- Actions -->
                  <template #item.actions="{ item }">
                    <v-btn
                      icon="mdi-pencil"
                      size="small"
                      variant="text"
                      color="primary"
                      @click="goToEdit(item.id)"
                    />
                    <v-btn
                      icon="mdi-eye"
                      size="small"
                      variant="text"
                      color="info"
                      @click="viewDetails(item)"
                    />
                  </template>
                </v-data-table>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>

    <!-- ================= DETAILS DIALOG ================= -->
    <v-dialog v-model="detailsDialog" max-width="900px">
      <v-card v-if="selectedItem">
        <v-card-title class="d-flex justify-space-between">
          Dyeing Order Details
          <v-btn icon="mdi-close" variant="text" @click="detailsDialog=false" />
        </v-card-title>

        <v-card-text>
          <v-row>
            <v-col md="6"><b>Code:</b> {{ selectedItem.code }}</v-col>
            <v-col md="6"><b>Style:</b> {{ selectedItem.style?.code }}</v-col>
            <v-col md="6"><b>Color:</b> {{ selectedItem.color?.name }}</v-col>
            <v-col md="6"><b>Dyeing House:</b> {{ selectedItem.dyeing_house?.name }}</v-col>
            <v-col md="6"><b>Order Qty (KG):</b> {{ selectedItem.order_qty_kg }}</v-col>
            <v-col md="6"><b>Dyeing Status:</b> {{ selectedItem.dyeing_status }}</v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="detailsDialog=false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-app>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { toast } from "vue3-toastify";

const store = useStore();
const router = useRouter();

const panel = ref([0]);
const search = ref("");
const loading = ref(false);
const itemsPerPage = ref(10);
const detailsDialog = ref(false);
const selectedItem = ref(null);

/* ================= FORM ================= */
const { handleSubmit } = useForm({
  validationSchema: yup.object({
    from_date: yup.date().nullable(),
    to_date: yup.date().nullable(),
  }),
});

const from_date = useField("from_date");
const to_date = useField("to_date");

/* ================= TABLE HEADERS ================= */
const headers = [
  { title: "#", key: "index", sortable: false },
  { title: "Code", key: "code" },
  { title: "Order Date", key: "order_date" },
  { title: "Delivery Date", key: "delivery_date" },
  { title: "Buyer", key: "buyer_name" },
  { title: "Dyeing House", key: "dyeing_house" },
  { title: "Dyeing Status", key: "dyeing_status" },
  { title: "Status", key: "status" },
  { title: "Actions", key: "actions", sortable: false },
];

const dyeingOrderList = computed(() =>
  store.getters.getDyeingOrderList || []
);

/* ================= METHODS ================= */
const submit = handleSubmit(() => {
  store.dispatch("getDyeingOrderList");
});

const goToCreate = () => {
  router.push({ name: "textile-order-create" });
};

const goToEdit = (id) => {
  router.push({ name: "textile-order-create", params: { id } });
};

const viewDetails = (item) => {
  router.push({ name: "textile-order-view", params: { id: item.id } });
};

const exportToCSV = () => toast.info("CSV export coming soon");
const exportToExcel = () => toast.info("Excel export coming soon");
const exportToPDF = () => toast.info("PDF export coming soon");

/* ================= ON MOUNT ================= */
onMounted(() => {
  loading.value = true;
  store.dispatch("getDyeingOrderList")
    .finally(() => loading.value = false);
});
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}
</style>
